import { useEffect, useRef, useState, type CSSProperties } from 'react';

/** Tracks the visual viewport, including mobile keyboard pan and resize. */
export function useMobileViewport(isMobile: boolean): CSSProperties {
  const [mobileViewportStyle, setMobileViewportStyle] = useState<CSSProperties>({});
  const mobileViewportKeyRef = useRef('');

  useEffect(() => {
    if (typeof window === 'undefined' || !isMobile) {
      mobileViewportKeyRef.current = '';
      setMobileViewportStyle({});
      return undefined;
    }

    const viewport = window.visualViewport;
    const syncMobileViewport = () => {
      const nextWidth = Math.max(0, Math.round(viewport?.width ?? window.innerWidth ?? 0));
      const nextHeight = Math.max(0, Math.round(viewport?.height ?? window.innerHeight ?? 0));
      const nextOffsetTop = Math.max(0, Math.round(viewport?.offsetTop ?? 0));
      const nextOffsetLeft = Math.max(0, Math.round(viewport?.offsetLeft ?? 0));
      const nextKey = `${nextWidth}:${nextHeight}:${nextOffsetTop}:${nextOffsetLeft}`;
      if (nextKey === mobileViewportKeyRef.current) return;
      mobileViewportKeyRef.current = nextKey;
      setMobileViewportStyle({
        '--rw-mobile-viewport-width': `${nextWidth}px`,
        '--rw-mobile-viewport-height': `${nextHeight}px`,
        '--rw-mobile-offset-top': `${nextOffsetTop}px`,
        '--rw-mobile-offset-left': `${nextOffsetLeft}px`,
      } as CSSProperties);
    };

    syncMobileViewport();
    window.addEventListener('resize', syncMobileViewport);
    window.addEventListener('orientationchange', syncMobileViewport);
    viewport?.addEventListener('resize', syncMobileViewport);
    viewport?.addEventListener('scroll', syncMobileViewport);

    return () => {
      window.removeEventListener('resize', syncMobileViewport);
      window.removeEventListener('orientationchange', syncMobileViewport);
      viewport?.removeEventListener('resize', syncMobileViewport);
      viewport?.removeEventListener('scroll', syncMobileViewport);
    };
  }, [isMobile]);

  return mobileViewportStyle;
}
